// src/pages/AdminOfficials.js
// Platform-operator console: game-official account approval queue. Officials
// sign up with accountType === "official" and start as officialStatus ===
// "pending"; an admin approves or denies them here. Approved officials can
// claim games and enter stats from /official. Gated by RoleGate require="admin"
// (server-enforced via ADMIN_UIDS on the admin Lambdas).

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "../services/api.service";
import AuthService from "../services/auth.service";

const C = {
  bg: "#1a1a19",
  surface: "#222220",
  surface2: "#2c2c29",
  border: "#333330",
  text: "#eaeae8",
  textDim: "#9a9994",
  accent: "#33cc66",
  blue: "#0A84FF",
};

const FILTERS = [
  { key: "", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "denied", label: "Denied" },
];

const STATUS_COLORS = {
  pending: "#f5a623",
  approved: "#33cc66",
  denied: "#ff6b6b",
};

function prettyDateTime(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return iso || "";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function AdminOfficials() {
  const navigate = useNavigate();
  const [officials, setOfficials] = useState([]);
  const [filter, setFilter] = useState(""); // '' = all
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ApiService.getAdminOfficials(filter || undefined);
      setOfficials(res?.officials || []);
    } catch (err) {
      setError(err.message || "Failed to load officials.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (official, officialStatus) => {
    if (updatingId) return;
    const name = `${official.firstName || ""} ${official.lastName || ""}`.trim() || official.email || official.userId;
    setUpdatingId(official.userId);
    setError(null);
    try {
      await ApiService.setAdminOfficialStatus(official.userId, officialStatus);
      setToast(`${name} ${officialStatus === "approved" ? "approved" : officialStatus === "denied" ? "denied" : "set to pending"}.`);
      setTimeout(() => setToast(null), 4000);
      await load();
    } catch (err) {
      setError(err.message || "Failed to update official status.");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleSignOut = async () => {
    try { await AuthService.logout(); } catch (_) {}
    navigate("/login", { replace: true });
  };

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text, fontFamily: "var(--font-primary, sans-serif)" }}>
      {/* Top bar */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "16px 24px", borderBottom: `1px solid ${C.border}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          <div style={{ fontSize: 18, fontWeight: 700 }}>Polyp</div>
          <nav style={{ display: "flex", gap: 6 }}>
            {/* REMOVED-CRM-ROUTES (2026-08-23) — these consoles' routes are
                disabled with the CRM (see App.js). Officials stays.
            <button onClick={() => navigate("/admin/matchmaking")} style={tab(false)}>Matchmaking</button>
            <button onClick={() => navigate("/admin/match-stats")} style={tab(false)}>Game Stats</button>
            <button onClick={() => navigate("/admin/placements")} style={tab(false)}>Awaiting Placement</button>
            <button onClick={() => navigate("/admin/assignments")} style={tab(false)}>Assignments</button>
            END-REMOVED-CRM-ROUTES */}
            <button onClick={() => navigate("/admin/officials")} style={tab(true)}>Officials</button>
          </nav>
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <button onClick={load} style={btn(C.surface2)}>Refresh</button>
          <button onClick={handleSignOut} style={btn(C.surface2)}>Sign out</button>
        </div>
      </div>

      <div style={{ maxWidth: 960, margin: "0 auto", padding: 24 }}>
        {toast && (
          <div style={{ background: `${C.accent}22`, color: C.accent, padding: "10px 14px", borderRadius: 10, marginBottom: 16 }}>
            {toast}
          </div>
        )}
        {error && (
          <div style={{ background: "#ff6b6b22", color: "#ff8f8f", padding: "10px 14px", borderRadius: 10, marginBottom: 16 }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
          <div style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: 1.2, color: C.textDim }}>
            Officials {loading ? "" : `(${officials.length})`}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {FILTERS.map((f) => (
              <button key={f.key || "all"} onClick={() => setFilter(f.key)} style={tab(filter === f.key)}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <p style={{ color: C.textDim }}>Loading…</p>
        ) : officials.length === 0 ? (
          <p style={{ color: C.textDim }}>
            {filter ? `No ${filter} officials.` : "No official accounts yet."}
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {officials.map((ref) => {
              const name = `${ref.firstName || ""} ${ref.lastName || ""}`.trim() || ref.email || ref.userId;
              const status = ref.officialStatus || "pending";
              const statusColor = STATUS_COLORS[status] || C.textDim;
              const busy = updatingId === ref.userId;
              return (
                <div key={ref.userId} style={{
                  background: C.surface, border: `1px solid ${C.border}`,
                  borderRadius: 12, padding: 16,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
                        {name}
                        <span style={{
                          marginLeft: 8, fontSize: 12, fontWeight: 700,
                          color: statusColor, background: `${statusColor}22`,
                          padding: "2px 8px", borderRadius: 999, verticalAlign: "middle",
                        }}>
                          {status}
                        </span>
                      </div>
                      <div style={{ color: C.textDim, fontSize: 14, display: "flex", flexWrap: "wrap", gap: "2px 16px" }}>
                        {ref.phoneNumber && <span>📱 {ref.phoneNumber}</span>}
                        {ref.email && <span>✉️ {ref.email}</span>}
                      </div>
                      <div style={{ color: C.textDim, fontSize: 12, marginTop: 6, opacity: 0.8 }}>
                        Signed up {prettyDateTime(ref.createdAt)}
                        {ref.officialStatusUpdatedAt ? ` · Status updated ${prettyDateTime(ref.officialStatusUpdatedAt)}` : ""}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        onClick={() => setStatus(ref, "approved")}
                        disabled={busy || status === "approved"}
                        style={btn(busy || status === "approved" ? C.surface2 : C.accent, busy || status === "approved" ? C.textDim : "#0b1f12")}
                      >
                        {busy ? "Updating…" : "Approve"}
                      </button>
                      <button
                        onClick={() => setStatus(ref, "denied")}
                        disabled={busy || status === "denied"}
                        style={btn(C.surface2, busy || status === "denied" ? C.textDim : "#ff8f8f")}
                      >
                        Deny
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function btn(bg, color = "#eaeae8") {
  return {
    background: bg, color, border: "none", borderRadius: 8,
    padding: "8px 14px", fontSize: 14, fontWeight: 600, cursor: "pointer",
  };
}
function tab(active) {
  return {
    background: active ? "#33cc6622" : "transparent",
    color: active ? "#33cc66" : "#9a9994",
    border: "none", borderRadius: 8, padding: "6px 12px",
    fontSize: 14, fontWeight: 600, cursor: "pointer",
  };
}
